import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { NotificationProvider } from './contexts/NotificationContext';
import { HelmetProvider, Helmet } from 'react-helmet-async';
import Header from './components/Header';
import Footer from './components/Footer';
import Sidebar from './components/Sidebar';
import TopNav from './components/TopNav';
import Home from './pages/Home';
import About from './pages/About';
import PersonalBanking from './pages/PersonalBanking';
import BusinessBanking from './pages/BusinessBanking';
import Loans from './pages/Loans';
import Insurance from './pages/Insurance';
import Contact from './pages/Contact';
import Services from './pages/Services';
import Login from './pages/Login';
import Register from './pages/Register';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import Dashboard from './pages/Dashboard';
import Accounts from './pages/Accounts';
import Transfer from './pages/Transfer';
import Zelle from './pages/Zelle';
import WireTransfer from './pages/WireTransfer';
import CryptoTrading from './pages/CryptoTrading';
import Cards from './pages/Cards';
import BillPay from './pages/BillPay';
import Support from './pages/Support';
import Profile from './pages/Profile';
import Deposit from './pages/Deposit';
import Privacy from './pages/Privacy';
import Terms from './pages/Terms';
import Security from './pages/Security';
import Accessibility from './pages/Accessibility';
import Sitemap from './pages/Sitemap';
import Disclosures from './pages/Disclosures';
import './App.css';

// ScrollToTop component to handle page navigation scroll behavior
const ScrollToTop = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
};

// Online Banking Redirect Component
const OnlineBankingRedirect = () => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }
  
  return isAuthenticated ? <Navigate to="/dashboard" replace /> : <Navigate to="/login" replace />;
};

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }
  
  return isAuthenticated ? children : <Navigate to="/login" />;
};

// Public Route Component (redirect to dashboard if authenticated)
const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }
  
  return !isAuthenticated ? children : <Navigate to="/dashboard" />;
};

// Marketing Route Component (accessible to both authenticated and unauthenticated users)
const MarketingRoute = ({ children }) => {
  const { loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }
  
  return children;
};

// Layout Component for authenticated pages with sidebar
const AuthenticatedLayout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Mobile Sidebar Overlay */}
      <Sidebar isOpen={sidebarOpen} setIsOpen={setSidebarOpen} />
      
      {/* Desktop Fixed Sidebar */}
      <div className="hidden lg:block lg:fixed lg:inset-y-0 lg:left-0 lg:w-80 lg:z-30">
        <Sidebar isOpen={true} setIsOpen={setSidebarOpen} isDesktop={true} />
      </div>
      
      {/* Main Content Area */}
      <div className="lg:pl-80">
        {/* Top Navigation */}
        <TopNav sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} />
        
        {/* Page Content */}
        <main className="min-h-screen pt-16">
          {children}
        </main>
      </div>
    </div>
  );
};

// Layout Component for public pages
const PublicLayout = ({ children }) => {
  return (
    <div className="min-h-screen bg-white">
      <Header />
      {children}
      <Footer />
    </div>
  );
};

function AppContent() {
  return (
    <HelmetProvider>
      <Router>
        <ScrollToTop />
        <div className="App min-h-screen flex flex-col">
          <Helmet>
                    <title>Aurora Wealth Reserves - Your Trusted Financial Partner</title>
        <meta name="description" content="Aurora Wealth Reserves offers comprehensive banking services, loans, mortgages, and investment solutions. Experience banking made simple, secure, and personal." />
          </Helmet>
          
          <Routes>
            {/* Public Routes */}
            <Route path="/" element={
              <PublicRoute>
                <PublicLayout>
                  <Home />
                </PublicLayout>
              </PublicRoute>
            } />
            <Route path="/about" element={
              <PublicRoute>
                <PublicLayout>
                  <About />
                </PublicLayout>
              </PublicRoute>
            } />
            <Route path="/personal-banking" element={
              <PublicRoute>
                <PublicLayout>
                  <PersonalBanking />
                </PublicLayout>
              </PublicRoute>
            } />
            <Route path="/business-banking" element={
              <PublicRoute>
                <PublicLayout>
                  <BusinessBanking />
                </PublicLayout>
              </PublicRoute>
            } />
            <Route path="/online-banking" element={
              <OnlineBankingRedirect />
            } />
            <Route path="/insurance-info" element={
              <MarketingRoute>
                <PublicLayout>
                  <Insurance />
                </PublicLayout>
              </MarketingRoute>
            } />
            <Route path="/contact" element={
              <PublicRoute>
                <PublicLayout>
                  <Contact />
                </PublicLayout>
              </PublicRoute>
            } />
            <Route path="/services" element={
              <MarketingRoute>
                <PublicLayout>
                  <Services />
                </PublicLayout>
              </MarketingRoute>
            } />

            {/* Legal & resource pages */}
            <Route path="/privacy" element={
              <MarketingRoute>
                <PublicLayout>
                  <Privacy />
                </PublicLayout>
              </MarketingRoute>
            } />
            <Route path="/terms" element={
              <MarketingRoute>
                <PublicLayout>
                  <Terms />
                </PublicLayout>
              </MarketingRoute>
            } />
            <Route path="/security" element={
              <MarketingRoute>
                <PublicLayout>
                  <Security />
                </PublicLayout>
              </MarketingRoute>
            } />
            <Route path="/accessibility" element={
              <MarketingRoute>
                <PublicLayout>
                  <Accessibility />
                </PublicLayout>
              </MarketingRoute>
            } />
            <Route path="/sitemap" element={
              <MarketingRoute>
                <PublicLayout>
                  <Sitemap />
                </PublicLayout>
              </MarketingRoute>
            } />
            <Route path="/disclosures" element={
              <MarketingRoute>
                <PublicLayout>
                  <Disclosures />
                </PublicLayout>
              </MarketingRoute>
            } />
            
            {/* Authentication Routes */}
            <Route path="/login" element={
              <PublicRoute>
                <Login />
              </PublicRoute>
            } />
            <Route path="/register" element={
              <PublicRoute>
                <Register />
              </PublicRoute>
            } />
            <Route path="/forgot-password" element={
              <PublicRoute>
                <ForgotPassword />
              </PublicRoute>
            } />
            <Route path="/reset-password" element={
              <PublicRoute>
                <ResetPassword />
              </PublicRoute>
            } />
            
            {/* Protected Routes - Banking Dashboard */}
            <Route path="/dashboard" element={
              <ProtectedRoute>
                <AuthenticatedLayout>
                  <Dashboard />
                </AuthenticatedLayout>
              </ProtectedRoute>
            } />
            
            {/* Account Management */}
            <Route path="/accounts" element={
              <ProtectedRoute>
                <AuthenticatedLayout>
                  <Accounts />
                </AuthenticatedLayout>
              </ProtectedRoute>
            } />
            
            {/* Banking Services */}
            <Route path="/transfer" element={
              <ProtectedRoute>
                <AuthenticatedLayout>
                  <Transfer />
                </AuthenticatedLayout>
              </ProtectedRoute>
            } />
            <Route path="/deposit" element={
              <ProtectedRoute>
                <AuthenticatedLayout>
                  <Deposit />
                </AuthenticatedLayout>
              </ProtectedRoute>
            } />
            <Route path="/zelle" element={
              <ProtectedRoute>
                <AuthenticatedLayout>
                  <Zelle />
                </AuthenticatedLayout>
              </ProtectedRoute>
            } />
            <Route path="/wire-transfer" element={
              <ProtectedRoute>
                <AuthenticatedLayout>
                  <WireTransfer />
                </AuthenticatedLayout>
              </ProtectedRoute>
            } />
            
            {/* Investment Services */}
            <Route path="/crypto-trading" element={
              <ProtectedRoute>
                <AuthenticatedLayout>
                  <CryptoTrading />
                </AuthenticatedLayout>
              </ProtectedRoute>
            } />
            <Route path="/investments" element={
              <ProtectedRoute>
                <AuthenticatedLayout>
                  <div className="p-6">
                    <div className="text-center">
                      <h1 className="text-3xl font-bold text-gray-900">Investment Portfolio</h1>
                      <p className="text-gray-600 mt-2">Coming Soon - Manage your investment accounts</p>
                    </div>
                  </div>
                </AuthenticatedLayout>
              </ProtectedRoute>
            } />
            <Route path="/market-analysis" element={
              <ProtectedRoute>
                <AuthenticatedLayout>
                  <div className="p-6">
                    <div className="text-center">
                      <h1 className="text-3xl font-bold text-gray-900">Market Analysis</h1>
                      <p className="text-gray-600 mt-2">Coming Soon - Real-time market insights and analysis</p>
                    </div>
                  </div>
                </AuthenticatedLayout>
              </ProtectedRoute>
            } />
            
            {/* Additional Services */}
            <Route path="/cards" element={
              <ProtectedRoute>
                <AuthenticatedLayout>
                  <Cards />
                </AuthenticatedLayout>
              </ProtectedRoute>
            } />
            <Route path="/bill-pay" element={
              <ProtectedRoute>
                <AuthenticatedLayout>
                  <BillPay />
                </AuthenticatedLayout>
              </ProtectedRoute>
            } />
            <Route path="/deposit" element={
              <ProtectedRoute>
                <AuthenticatedLayout>
                  <div className="p-6">
                    <div className="text-center">
                      <h1 className="text-3xl font-bold text-gray-900">Mobile Check Deposit</h1>
                      <p className="text-gray-600 mt-2">Coming Soon - Deposit checks using your mobile device</p>
                    </div>
                  </div>
                </AuthenticatedLayout>
              </ProtectedRoute>
            } />
            <Route path="/loans" element={
              <MarketingRoute>
                <PublicLayout>
                  <Loans />
                </PublicLayout>
              </MarketingRoute>
            } />
            <Route path="/insurance" element={
              <ProtectedRoute>
                <AuthenticatedLayout>
                  <div className="p-6">
                    <div className="max-w-4xl mx-auto">
                      <div className="text-center mb-8">
                        <h1 className="text-3xl font-bold text-gray-900 mb-4">Insurance Services</h1>
                        <p className="text-gray-600">Manage your insurance policies and get quotes</p>
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                        <div className="bg-white rounded-lg shadow-md p-6">
                          <h3 className="text-xl font-semibold text-gray-900 mb-3">My Policies</h3>
                          <p className="text-gray-600 mb-4">View and manage your insurance policies</p>
                          <div className="text-sm text-gray-500">No active policies found</div>
                        </div>
                        
                        <div className="bg-white rounded-lg shadow-md p-6">
                          <h3 className="text-xl font-semibold text-gray-900 mb-3">Get Quote</h3>
                          <p className="text-gray-600 mb-4">Get quotes for insurance coverage</p>
                          <a href="/insurance-info" className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
                            View Insurance Options
                          </a>
                        </div>
                      </div>
                    </div>
                  </div>
                </AuthenticatedLayout>
              </ProtectedRoute>
            } />
            <Route path="/support" element={
              <ProtectedRoute>
                <AuthenticatedLayout>
                  <Support />
                </AuthenticatedLayout>
              </ProtectedRoute>
            } />
            <Route path="/profile" element={
              <ProtectedRoute>
                <AuthenticatedLayout>
                  <Profile />
                </AuthenticatedLayout>
              </ProtectedRoute>
            } />
            
            {/* Legacy Routes (for backward compatibility) */}
            <Route path="/crypto" element={<Navigate to="/crypto-trading" replace />} />
            <Route path="/online-bank" element={<OnlineBankingRedirect />} />
            <Route path="/onlinebanking" element={<OnlineBankingRedirect />} />
            <Route path="/internet-banking" element={<OnlineBankingRedirect />} />
            <Route path="/web-banking" element={<OnlineBankingRedirect />} />
            
            {/* Catch all route */}
            <Route path="*" element={<Navigate to="/" />} />
          </Routes>
        </div>
      </Router>
    </HelmetProvider>
  );
}

function App() {
  return (
    <AuthProvider>
      <NotificationProvider>
        <AppContent />
      </NotificationProvider>
    </AuthProvider>
  );
}

export default App;
